import type { TextFieldProps } from "@mui/material";

// MUI v9 removed `InputProps`/`inputProps` from TextField in favour of
// `slotProps.input`/`slotProps.htmlInput`. Our TextField wrapper components accept the
// legacy names for backward compatibility and normalize them here into `slotProps`.
export type LegacyTextFieldProps = Omit<TextFieldProps, "slotProps"> & {
  InputProps?: Record<string, unknown>;
  inputProps?: Record<string, unknown>;
  SelectProps?: Record<string, unknown>;
  slotProps?: TextFieldProps["slotProps"];
};

type SlotInput = NonNullable<TextFieldProps["slotProps"]>["input"];
type SlotHtmlInput = NonNullable<TextFieldProps["slotProps"]>["htmlInput"];
type SlotSelect = NonNullable<TextFieldProps["slotProps"]>["select"];

/** Fold legacy `InputProps`/`inputProps`/`SelectProps` into their `slotProps` equivalents. */
export const normalizeTextFieldProps = (
  props?: LegacyTextFieldProps
): TextFieldProps => {
  if (!props) {
    return {} as TextFieldProps;
  }
  const { InputProps, inputProps, SelectProps, slotProps, ...rest } = props;
  if (!InputProps && !inputProps && !SelectProps) {
    return props as TextFieldProps;
  }
  const mergedInput =
    InputProps && typeof slotProps?.input === "object"
      ? { ...slotProps.input, ...InputProps }
      : (InputProps ?? slotProps?.input);
  const mergedHtmlInput =
    inputProps && typeof slotProps?.htmlInput === "object"
      ? { ...slotProps.htmlInput, ...inputProps }
      : (inputProps ?? slotProps?.htmlInput);
  const mergedSelect =
    SelectProps && typeof slotProps?.select === "object"
      ? { ...slotProps.select, ...SelectProps }
      : (SelectProps ?? slotProps?.select);
  return {
    ...rest,
    slotProps: {
      ...slotProps,
      ...(mergedInput ? { input: mergedInput as SlotInput } : {}),
      ...(mergedHtmlInput
        ? { htmlInput: mergedHtmlInput as SlotHtmlInput }
        : {}),
      ...(mergedSelect ? { select: mergedSelect as SlotSelect } : {}),
    },
  } as TextFieldProps;
};
